/**
 * Store UI éphémère — Phase 1, incrément 7.
 *
 * État qui ne mérite pas de persistance : modal active, sidebar
 * ouverte/fermée (futur), focus, etc. Reset à chaque rechargement.
 *
 * Pour l'instant : un seul champ `modal` qui identifie la modale ouverte
 * dans la vue courante. La vue qui possède la modale décide du sens
 * (ex. `'event'` pour Planning, `'transmission'` pour Liaison).
 *
 * Les vues s'abonnent via `subscribeUi` ; l'AppShell s'abonne aussi
 * pour redessiner le shell complet à chaque changement.
 */
import { createStore } from '../lib/store.js';

export type ModalKey = string | null;

interface UiState {
  modal: ModalKey;
  /** Compteur monotone — incrémenté par `bumpUi()` pour forcer un cycle
   *  de rendu quand l'état d'un composant local (recording, attachment
   *  pending) change sans passer par un store domaine. */
  tick: number;
}

const uiStore = createStore<UiState>({ modal: null, tick: 0 });

// Handler global Escape — installé tant qu'une modale est ouverte. On
// préfère ce niveau central plutôt que `@keydown` sur la backdrop : ce
// dernier n'est pas déclenché si le focus est ailleurs (ex. modal qui
// vient de s'ouvrir et que l'utilisateur n'a pas cliqué dedans).
function escapeHandler(e: KeyboardEvent): void {
  if (e.key === 'Escape') closeModal();
}

function safeWindow(): Window | undefined {
  return typeof window === 'undefined' ? undefined : window;
}

export function getModal(): ModalKey {
  return uiStore.getState().modal;
}

export function openModal(key: NonNullable<ModalKey>): void {
  if (uiStore.getState().modal === key) return;
  const wasOpen = uiStore.getState().modal !== null;
  uiStore.setState({ modal: key });
  if (!wasOpen) {
    safeWindow()?.addEventListener('keydown', escapeHandler);
  }
}

export function closeModal(): void {
  if (uiStore.getState().modal === null) return;
  uiStore.setState({ modal: null });
  safeWindow()?.removeEventListener('keydown', escapeHandler);
}

export function subscribeUi(listener: (state: UiState) => void): () => void {
  return uiStore.subscribe(listener);
}

/**
 * Force un cycle de re-render via incrément du tick. À utiliser quand
 * une vue mute un état local (recording voice, attachment pending,
 * picker éphémère) qui ne vit pas dans un store. À éviter pour tout
 * état persistant : si la valeur mérite un sélecteur typé, créer un
 * store dédié.
 */
export function bumpUi(): void {
  uiStore.setState((s) => ({ tick: s.tick + 1 }));
}

/** @internal pour tests. */
export function _resetUiForTests(): void {
  uiStore.setState({ modal: null, tick: 0 }, true);
}
